"use client";

import {
  CircleCheck,
  CircleHelp,
  CircleX,
  Clock,
  Copy,
  Loader2,
  Lock,
  ServerCrash,
  Sparkles,
  Trash2,
  TriangleAlert,
  Wand2,
} from "lucide-react";
import { useTranslations } from "next-intl";
import { useCallback, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import type { ExecutionResult } from "../types";
import {
  CLIENT_EXECUTION_OUTPUT_LIMIT_BYTES,
  hasCompileIssue,
  kindFromResult,
  RUN_STATUS_LABEL_KEY,
  RUN_STATUS_LABEL_VALUES,
  toneFromKind,
  type RunStatusKind,
} from "../lib/run-status";
import { cn } from "@/lib/utils";

type TabId = "output" | "compile";

/** The tablist's order, which the arrow keys walk (WAI-ARIA tabs pattern). */
const TAB_ORDER = ["output", "compile"] as const satisfies readonly TabId[];

/**
 * `PG-UX03`: an icon per kind so the status is never carried by colour alone.
 * The memory and output limits share the warning triangle because they share
 * the tone and the remedy; the label tells them apart.
 */
const STATUS_ICON: Record<RunStatusKind, typeof CircleCheck> = {
  success: CircleCheck,
  runtime_error: CircleX,
  compilation_error: CircleX,
  time_limit_exceeded: Clock,
  memory_limit_exceeded: TriangleAlert,
  output_limit_exceeded: TriangleAlert,
  internal_error: ServerCrash,
  unknown: CircleHelp,
};

type PlaygroundOutputProps = {
  result: ExecutionResult | null;
  runError: string | null;
  isRunning: boolean;
  onClear: () => void;
  fontSize: number;
  /** IDE Pro, Explain My Code (output panel) */
  explain?: {
    onClick: () => void;
    locked: boolean;
    disabled: boolean;
    /** Why it is off when the buffer is not empty (offline, `PG-PWA01`). */
    disabledReason?: string;
  };
  /**
   * Fix My Error (`PG-AI02`). It lives in the status bar rather than the
   * toolbar row: the row above already carries three buttons at `sm`, and the
   * hint line is where a kind-conditional call to action belongs, beside the
   * "not your code's fault" sentence that answers the other failure kinds.
   */
  fixError?: {
    onClick: () => void;
    /** Only a compile or runtime error with a diagnostic (`isFixEligible`). */
    eligible: boolean;
  };
};

export function PlaygroundOutput({
  result,
  runError,
  isRunning,
  onClear,
  fontSize,
  explain,
  fixError,
}: PlaygroundOutputProps) {
  const t = useTranslations("playground");
  const [tab, setTab] = useState<TabId>("output");
  const outputTabRef = useRef<HTMLButtonElement>(null);
  const compileTabRef = useRef<HTMLButtonElement>(null);
  const TAB_REFS = useMemo(
    () => ({ output: outputTabRef, compile: compileTabRef }) as const,
    [],
  );

  /**
   * Roving-tabindex arrow-key switching (WAI-ARIA tabs pattern).
   *
   * Driven off the tab order rather than a two-way toggle: Left/Right step and
   * wrap, Home and End go to the ends. With two tabs the behaviour is identical
   * to the toggle it replaces; with a third it stays correct instead of
   * silently ignoring it.
   */
  const onTablistKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      const index = TAB_ORDER.indexOf(tab);
      let nextIndex: number;
      if (e.key === "ArrowLeft") {
        nextIndex = (index - 1 + TAB_ORDER.length) % TAB_ORDER.length;
      } else if (e.key === "ArrowRight") {
        nextIndex = (index + 1) % TAB_ORDER.length;
      } else if (e.key === "Home") {
        nextIndex = 0;
      } else if (e.key === "End") {
        nextIndex = TAB_ORDER.length - 1;
      } else {
        return;
      }
      e.preventDefault();
      const next = TAB_ORDER[nextIndex];
      setTab(next);
      TAB_REFS[next].current?.focus();
    },
    [tab, TAB_REFS],
  );

  const compileVisible = hasCompileIssue(result);
  const kind = result ? kindFromResult(result) : null;
  const tone = kind ? toneFromKind(kind) : runError ? "error" : "neutral";
  const StatusIcon = kind ? STATUS_ICON[kind] : null;
  const statusLabel = kind ? t(RUN_STATUS_LABEL_KEY[kind], RUN_STATUS_LABEL_VALUES) : "";

  const outputBody = useMemo(() => {
    if (!result) return "";
    const parts: string[] = [];
    if (result.stdout) parts.push(result.stdout);
    if (result.stderr) parts.push(result.stderr);
    return parts.join("\n").trimEnd();
  }, [result]);

  const copyActive = useCallback(() => {
    const text =
      tab === "compile"
        ? result?.compileOutput ?? ""
        : runError ?? (outputBody || result?.stderr || "");
    if (!text) {
      toast.message(t("copyNothing"));
      return;
    }
    void navigator.clipboard.writeText(text).then(
      () => toast.success(t("toastCopied")),
      () => toast.error(t("toastCopyFailed")),
    );
  }, [outputBody, result?.compileOutput, result?.stderr, runError, tab, t]);

  const badgeClass =
    tone === "success"
      ? "bg-emerald-500/15 text-emerald-300 ring-1 ring-emerald-500/40"
      : tone === "warning"
        ? "bg-amber-500/15 text-amber-200 ring-1 ring-amber-500/40"
        : tone === "error"
          ? "bg-red-500/15 text-red-300 ring-1 ring-red-500/40"
          : "bg-white/10 text-white/60 ring-1 ring-white/15";

  return (
    <div
      className={cn(
        "flex min-h-0 min-w-0 w-full flex-1 flex-col rounded-lg border border-white/10 bg-[#0B0B0C]",
        runError && !isRunning && "border-red-500/50",
      )}
    >
      <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-white/10 px-3 py-2">
        <div
          role="tablist"
          aria-label={t("outputTabsLabel")}
          onKeyDown={onTablistKeyDown}
          className="flex gap-1 rounded-lg bg-white/5 p-0.5"
        >
          <button
            ref={outputTabRef}
            type="button"
            role="tab"
            id="pg-tab-output"
            aria-selected={tab === "output"}
            aria-controls="pg-output-panel"
            tabIndex={tab === "output" ? 0 : -1}
            onClick={() => setTab("output")}
            className={cn(
              "rounded-md px-2.5 py-1 text-xs font-medium transition-colors",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E94D1D]/45",
              tab === "output" ? "bg-white/10 text-white" : "text-white/50 hover:text-white/80",
            )}
          >
            {t("outputTab")}
          </button>
          <button
            ref={compileTabRef}
            type="button"
            role="tab"
            id="pg-tab-compile"
            aria-selected={tab === "compile"}
            aria-controls="pg-output-panel"
            tabIndex={tab === "compile" ? 0 : -1}
            onClick={() => setTab("compile")}
            className={cn(
              "rounded-md px-2.5 py-1 text-xs font-medium transition-colors",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E94D1D]/45",
              tab === "compile" ? "bg-white/10 text-white" : "text-white/50 hover:text-white/80",
              compileVisible && "text-amber-200/90",
            )}
          >
            {t("compileTab")}
          </button>
        </div>
        <div className="ml-auto flex items-center gap-1.5">
          {explain ? (
            <button
              type="button"
              onClick={explain.onClick}
              disabled={explain.disabled}
              title={
                explain.disabled
                  ? (explain.disabledReason ?? t("explainDisabledEmpty"))
                  : explain.locked
                    ? t("explainLocked")
                    : t("explainAria")
              }
              aria-label={explain.locked ? t("explainLocked") : t("explainAria")}
              className={cn(
                "inline-flex h-8 items-center gap-1 rounded-lg px-2 text-xs transition",
                explain.disabled
                  ? "cursor-not-allowed text-white/35"
                  : "text-violet-200/95 hover:bg-violet-500/15 hover:text-white",
              )}
            >
              {explain.locked ? (
                <Lock className="h-3.5 w-3.5 shrink-0" aria-hidden />
              ) : (
                <Sparkles className="h-3.5 w-3.5 shrink-0 text-violet-300" aria-hidden />
              )}
              <span className="hidden sm:inline">{t("explain")}</span>
            </button>
          ) : null}
          <button
            type="button"
            onClick={copyActive}
            className="inline-flex h-8 items-center gap-1 rounded-lg px-2 text-xs text-white/70 transition hover:bg-white/10 hover:text-white"
          >
            <Copy className="h-3.5 w-3.5" aria-hidden />
            {t("copy")}
          </button>
          <button
            type="button"
            onClick={onClear}
            className="inline-flex h-8 items-center gap-1 rounded-lg px-2 text-xs text-white/70 transition hover:bg-white/10 hover:text-white"
          >
            <Trash2 className="h-3.5 w-3.5" aria-hidden />
            {t("clear")}
          </button>
        </div>
      </div>

      {result?.outputTruncated && !isRunning && !runError ? (
        <div
          role="status"
          className="flex shrink-0 items-start gap-2 border-b border-amber-400/20 bg-amber-400/10 px-3 py-2 text-xs text-amber-100"
        >
          <TriangleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
          <span>
            {t("outputTruncated", {
              limitKb: CLIENT_EXECUTION_OUTPUT_LIMIT_BYTES / 1024,
            })}
          </span>
        </div>
      ) : null}

      {/*
        Capped below `lg` (`PG-UX08`): the stacked phone layout scrolls the page,
        so nothing above bounds this box there, and a 100 KB output grew it to
        about 196,000 px with the status bar under all of it. From `lg` up the
        route layout bounds the panes (`PG-UX07`) and the cap lifts. Written as a
        base class plus `lg:max-h-none` because the `max-` variants are not
        generated here: the screens config holds an object (`short`), which
        disables every `min-` and `max-` variant.
      */}
      <div
        role="tabpanel"
        id="pg-output-panel"
        aria-labelledby={tab === "output" ? "pg-tab-output" : "pg-tab-compile"}
        className="relative min-h-0 min-w-0 max-h-[50dvh] flex-1 overflow-auto p-3 lg:max-h-none"
      >
        {isRunning ? (
          <div className="flex h-full min-h-[120px] flex-col items-center justify-center gap-3 text-white/50">
            <Loader2 className="h-8 w-8 animate-spin text-[#E94D1D]" aria-hidden />
            <span className="text-sm">{t("running")}</span>
          </div>
        ) : runError ? (
          <pre
            style={{ fontSize: `${fontSize}px` }}
            className="whitespace-pre-wrap break-words font-mono text-red-300"
          >
            {runError}
          </pre>
        ) : tab === "compile" ? (
          <pre
            style={{ fontSize: `${fontSize}px` }}
            className="whitespace-pre-wrap break-words font-mono text-white/85"
          >
            {result?.compileOutput?.trim() ? result.compileOutput : t("compileEmpty")}
          </pre>
        ) : (
          <pre
            style={{ fontSize: `${fontSize}px` }}
            className="whitespace-pre-wrap break-words font-mono text-white/90"
          >
            {outputBody || (result ? t("outputEmpty") : t("outputPlaceholder"))}
          </pre>
        )}
      </div>

      {/*
        The run outcome lands here and nowhere else, so this row is the live
        region: without it a screen-reader user presses Run and hears nothing
        at all. Polite, because the result is not an interruption, and scoped to
        the status bar so only the outcome, timings and runtime are read.
      */}
      <div
        role="status"
        aria-live="polite"
        className="flex shrink-0 flex-wrap items-center gap-2 border-t border-white/10 px-3 py-2 text-xs text-white/55"
      >
        {result && kind && StatusIcon && !isRunning && !runError && (
          <>
            {/*
              PG-UX03: the label comes from the semantic kind and the locale,
              never from the engine's prose. The prose survives only as a
              tooltip, and inline when the kind is unknown, because then it is
              the one thing the student can quote to support.
            */}
            <span
              className={cn(
                "inline-flex items-center gap-1 rounded px-2 py-0.5 font-medium",
                badgeClass,
              )}
              title={result.status !== statusLabel ? result.status : undefined}
            >
              <StatusIcon className="h-3.5 w-3.5 shrink-0" aria-hidden />
              <span className="sr-only">{t("statusBadgeLabel", { status: statusLabel })}</span>
              <span aria-hidden>{statusLabel}</span>
            </span>
            {kind === "unknown" && result.status ? (
              <span className="truncate text-white/45">
                {t("statusEngineSaid", { raw: result.status })}
              </span>
            ) : null}
            {result.timeMs != null && (
              <span>
                {t("statTime")}: {result.timeMs} ms
              </span>
            )}
            {result.memoryKb != null && (
              <span>
                {t("statMemory")}: {result.memoryKb} KB
              </span>
            )}
            {result.exitCode != null && (
              <span>
                {t("statExit")}: {result.exitCode}
              </span>
            )}
            {/*
              What actually ran, straight off the response, not the version the
              client believes it asked for. A selector that reports a version
              nobody verified is the defect PG-IDE01 exists to remove.
            */}
            {result.version && (
              <span
                className={cn(
                  "ml-auto truncate",
                  result.version.fellBackFrom ? "text-amber-300" : "text-white/45",
                )}
                title={result.version.toolchain}
              >
                {result.version.fellBackFrom
                  ? t("runtimeRanFallback", {
                      version: result.version.label,
                      requested: result.version.fellBackFrom,
                    })
                  : result.version.supersededFrom
                    ? // Not amber: nothing went wrong. A pin that was retired
                      // and mapped up is an announcement, and the line says
                      // which version the code was saved against so the student
                      // can see the bump rather than infer it.
                      t("runtimeRanSuperseded", {
                        version: result.version.label,
                        requested: result.version.supersededFrom,
                      })
                    : t("runtimeRanOn", { version: result.version.label })}
              </span>
            )}
            {kind === "internal_error" || kind === "unknown" ? (
              <span className="basis-full text-white/65">
                {t("statusRetryHint")}
              </span>
            ) : null}
            {fixError?.eligible ? (
              <span className="flex basis-full items-center gap-2 pt-0.5">
                <span className="text-white/45">{t("fixErrorPrompt")}</span>
                <button
                  type="button"
                  onClick={fixError.onClick}
                  className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-[#FFAB8F] transition hover:bg-[#E94D1D]/15 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E94D1D]/45"
                >
                  <Wand2 className="h-3.5 w-3.5 shrink-0" aria-hidden />
                  {t("fixError")}
                </button>
              </span>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}
